import { Injectable, OnDestroy } from '@angular/core';
import { StateService } from './state.service';
import {
  catchError,
  finalize,
  forkJoin,
  Observable,
  Subscription,
  tap,
  throwError,
} from 'rxjs';
import {
  GetPlanogramBodyParams,
  PlanogramModal,
  SKUModal,
  PlanogramBaseDetailModal,
  CoolerTemplateBaseDataModal,
  ScreenDataModal,
  APIErrorObj,
  PlanogramBaseDetailInfo,
  PlanogramNPDDetailInfo,
  adminCoolerDropDown,
  adminCooler,
  adminSku,
  AdminBaseData,
  planogramsModel,
  comparablePlanogramsModel,
  reviewSkuModal,
  ReviewKPIs,
  PlanogramSegmentNPDModalWithCount,
  UpdateDatasetModal,
  UserAnalyticsDataModal,
} from 'src/modals/planogram';
import { PlanogramService } from './planogram.service';
import { Router } from '@angular/router';
import { CONFIG } from 'src/helper/app-config';
import {
  getPlanogramImageUrl,
  sortSkusAccordingToBrandOrderAndSize,
} from 'src/helper/app-utils';
import {
  CommandService,
  SkuCommandFactory,
  SkuCommandMap,
} from './command.service';
import { environment } from 'src/environments/environment';
import { environment as dev_environment } from 'src/environments/environment.dev';
import { AzureBlobStorageService } from './azure-blob-storage.service';
import { ConfirmDialogModalComponent } from 'src/components/confirm-dialog-modal/confirm-dialog-modal.component';
import { MatDialog } from '@angular/material/dialog';
import * as htmlToImage from 'html-to-image';
import {
  drop_down_data,
  npd_data,
  admin_cooler_dropdown_data,
} from 'src/helper/static';
import { AdminDialogComponent } from 'src/components/admin-dialog/admin-dialog.component';
import html2canvas from 'html2canvas';

interface PlanogramState {
  is_loader: boolean;
  loading_count: number;
  planogram_loading_count: number;
  sku_list: SKUModal[];
  sku_info: Object;
  cooler_config: PlanogramModal;
  placed_sku_list: SKUModal[];
  next_event_api_response: boolean;
  planogram_unique_name_error: string;
  planogram_base_details: PlanogramBaseDetailInfo;
  npd_data: Object;
  saved_planogram?: planogramsModel;
  draft_planogram: Object;
  is_more_planograms: boolean;
  is_planogram_edit: boolean;
  edit_planogram_detail: any;
  selected_planogram_base_data: Object;
  cooler_template_base_data: CoolerTemplateBaseDataModal;
  specific_screen_data: ScreenDataModal[];
  template_unique_name_error: string;
  note_for_blank_sku: string;
  create_new_template_success: boolean;
  selected_cooler_template: Object;
  selected_cooler_template_base_data: Object;
  saved_templates: Object;
  error_obj: APIErrorObj;
  is_update_highlight_sku_placeholder: boolean;
  picos_skus: SKUModal[];
  selected_planogram_image: string;
  compare_planogram_data: Object;
  recommandations: any[];
  compare_planogram: comparablePlanogramsModel;
  throughput_metrics: any[];
  autofill_throughput_metrics: any[];
  close_copy_planogram_dialog: boolean;
  brandchartcolor: any;
  categorychartcolor: any;
  planogram_metrics: any;
  brands: any;
  npd: PlanogramNPDDetailInfo;
  adminCoolerDropDownData: adminCoolerDropDown;
  adminCoolerData: adminCooler[];
  adminSkuData: adminSku[];
  adminBaseData: AdminBaseData[];
  adminBaseDataBasedonFileType: any;
  reviewSkuList: reviewSkuModal;
  reviewKpis: ReviewKPIs;
  analysisKPIs: any;
  reviewLhsSkuList: any;
  coolerInfo: any;
  guidelinesInput: any;
  saved_planogram_landing_page_info: any;
  user_info: any;
  adminUpdateData: UpdateDatasetModal[];
  adminUserAnalyticsData: UserAnalyticsDataModal[];
  adminUserQueriesData: UserAnalyticsDataModal[];
  guidelines_library_data: any;
  versioned_guidelines: any;
  customPlanogramLibrary: any;
  reviewScreenSkuImages: any;
  planogramLoader: boolean;
}

const initialState: PlanogramState = {
  is_loader: false,
  loading_count: 0,
  planogram_loading_count: 0,
  sku_list: [],
  sku_info: {},
  cooler_config: {} as any,
  is_planogram_edit: false,
  edit_planogram_detail: {},
  placed_sku_list: [],
  next_event_api_response: false,
  planogram_unique_name_error: '',
  planogram_base_details: {} as any,
  saved_planogram: undefined,
  draft_planogram: {},
  npd_data: {},
  is_more_planograms: true,
  selected_planogram_base_data: {} as any,
  cooler_template_base_data: {} as any,
  specific_screen_data: [],
  template_unique_name_error: '',
  note_for_blank_sku: '',
  create_new_template_success: false,
  selected_cooler_template: {} as any,
  selected_cooler_template_base_data: {} as any,
  saved_templates: {},
  error_obj: { is_error: false, error_msg: '' },
  is_update_highlight_sku_placeholder: false,
  picos_skus: [],
  selected_planogram_image: '',
  compare_planogram_data: {} as any,
  recommandations: [],
  compare_planogram: {} as comparablePlanogramsModel,
  throughput_metrics: [],
  autofill_throughput_metrics: [],
  close_copy_planogram_dialog: false,
  brandchartcolor: {},
  categorychartcolor: {},
  planogram_metrics: {},
  brands: {},
  npd: {} as any,
  adminCoolerDropDownData: {} as any,
  adminCoolerData: {} as adminCooler[],
  adminSkuData: {} as adminSku[],
  adminBaseData: [] as AdminBaseData[],
  adminBaseDataBasedonFileType: [],
  reviewSkuList: {} as reviewSkuModal,
  reviewKpis: {} as ReviewKPIs,
  analysisKPIs: {} as any,
  reviewLhsSkuList: {} as any,
  coolerInfo: {} as any,
  guidelinesInput: {} as any,
  saved_planogram_landing_page_info: {} as any,
  user_info: {} as any,
  adminUpdateData: [] as UpdateDatasetModal[],
  adminUserAnalyticsData: [] as UserAnalyticsDataModal[],
  adminUserQueriesData: [] as UserAnalyticsDataModal[],
  guidelines_library_data: [] as any,
  versioned_guidelines: {} as any,
  customPlanogramLibrary: [] as any,
  reviewScreenSkuImages: {} as any,
  planogramLoader: false as boolean,
};

@Injectable({
  providedIn: 'root',
})
export class PlanogramStateService
  extends StateService<PlanogramState>
  implements OnDestroy
{
  available_skus: Observable<SKUModal[]> = this.select(
    (state) => state.sku_list
  );
  sku_info: Observable<Object> = this.select((state) => state.sku_info);

  planogram_placed_skus: Observable<SKUModal[]> = this.select(
    (state) => state.placed_sku_list
  );
  cooler_config_info: Observable<PlanogramModal> = this.select(
    (state) => state.cooler_config
  );
  loader: Observable<boolean> = this.select((state) => state.is_loader);

  next_event_api_response: Observable<boolean> = this.select(
    (state) => state.next_event_api_response
  );

  planogram_name_error: Observable<string> = this.select(
    (state) => state.planogram_unique_name_error
  );
  planogram_base_details: Observable<PlanogramBaseDetailInfo> = this.select(
    (state) => state.planogram_base_details
  );
  all_planogram: Observable<planogramsModel> = this.select(
    (state) => state.saved_planogram!
  );
  is_more_planograms: Observable<boolean> = this.select(
    (state) => state.is_more_planograms
  );
  is_planogram_edit: Observable<boolean> = this.select(
    (state) => state.is_planogram_edit
  );
  edit_planogram_detail: Observable<any> = this.select(
    (state) => state.edit_planogram_detail
  );
  selected_planogram_base_data: Observable<Object> = this.select(
    (state) => state.selected_planogram_base_data
  );
  cooler_template_base_data: Observable<CoolerTemplateBaseDataModal> =
    this.select((state) => state.cooler_template_base_data);
  specific_screen_data: Observable<ScreenDataModal[]> = this.select(
    (state) => state.specific_screen_data
  );
  template_unique_name_error: Observable<string> = this.select(
    (state) => state.template_unique_name_error
  );
  note_for_blank_sku: Observable<string> = this.select(
    (state) => state.note_for_blank_sku
  );
  create_new_template_success: Observable<boolean> = this.select(
    (state) => state.create_new_template_success
  );
  selected_cooler_template: Observable<any> = this.select(
    (state) => state.selected_cooler_template
  );
  selected_cooler_template_base_data: Observable<any> = this.select(
    (state) => state.selected_cooler_template_base_data
  );
  saved_templates: Observable<any> = this.select(
    (state) => state.saved_templates
  );
  error_obj: Observable<APIErrorObj> = this.select((state) => state.error_obj);
  is_update_highlight_sku_placeholder: Observable<boolean> = this.select(
    (state) => state.is_update_highlight_sku_placeholder
  );
  picos_skus: Observable<SKUModal[]> = this.select((state) => state.picos_skus);
  selected_planogram_image: Observable<string> = this.select(
    (state) => state.selected_planogram_image
  );
  compare_planogram_data: Observable<Object> = this.select(
    (state) => state.compare_planogram_data
  );
  recommandations: Observable<any[]> = this.select(
    (state) => state.recommandations
  );
  compare_planogram: Observable<comparablePlanogramsModel> = this.select(
    (state) => state.compare_planogram
  );
  throughput_metrics: Observable<any[]> = this.select(
    (state) => state.throughput_metrics
  );
  autofill_throughput_metrics: Observable<any[]> = this.select(
    (state) => state.autofill_throughput_metrics
  );
  close_copy_planogram_dialog: Observable<boolean> = this.select(
    (state) => state.close_copy_planogram_dialog
  );
  brandchartcolor: Observable<any> = this.select(
    (state) => state.brandchartcolor
  );
  categorychartcolor: Observable<any> = this.select(
    (state) => state.categorychartcolor
  );
  planogram_metrics: Observable<any> = this.select(
    (state) => state.planogram_metrics
  );
  sku_brands: Observable<any> = this.select((state) => state.brands);

  review_sku_list: Observable<reviewSkuModal> = this.select(
    (state) => state.reviewSkuList
  );

  review_kpis: Observable<ReviewKPIs> = this.select(
    (state) => state.reviewKpis
  );

  admin_cooler_dropdown_data: Observable<any> = this.select(
    (state) => state.adminCoolerDropDownData
  );
  admin_cooler_data: Observable<any> = this.select(
    (state) => state.adminCoolerData
  );
  admin_sku_data: Observable<any> = this.select((state) => state.adminSkuData);
  admin_base_data: Observable<any> = this.select(
    (state) => state.adminBaseData
  );
  admin_base_data_based_on_type_of_file: Observable<any> = this.select(
    (state) => state.adminBaseDataBasedonFileType
  );

  npd_data: Observable<PlanogramNPDDetailInfo> = this.select(
    (state) => state.npd
  );

  review_lhs_sku_list: Observable<any> = this.select(
    (state) => state.reviewLhsSkuList
  );

  cooler_info: Observable<any> = this.select((state) => state.coolerInfo);
  guidelines_input: Observable<any> = this.select(
    (state) => state.guidelinesInput
  );

  analysis_kpis: Observable<any> = this.select((state) => state.analysisKPIs);
  saved_planogram_landing_page_info_data: Observable<any> = this.select(
    (state) => state.saved_planogram_landing_page_info
  );

  userInfo: Observable<any> = this.select((state) => state.user_info);
  admin_update_data: Observable<any> = this.select(
    (state) => state.adminUpdateData
  );
  admin_user_analytics_data: Observable<any> = this.select(
    (state) => state.adminUserAnalyticsData
  );
  admin_user_queries_data: Observable<any> = this.select(
    (state) => state.adminUserQueriesData
  );

  admin_guidelines_library_data: Observable<any> = this.select(
    (state) => state.guidelines_library_data
  );
  versioned_guidelines_data: Observable<any> = this.select(
    (state) => state.versioned_guidelines
  );
  custom_planogram_library_data: Observable<any> = this.select(
    (state) => state.customPlanogramLibrary
  );

  review_screen_sku_images: Observable<any> = this.select(
    (state) => state.reviewScreenSkuImages
  );

  planogram_loader: Observable<any> = this.select(
    (state) => state.planogramLoader
  );

  private commandFactory: SkuCommandFactory;
  private commandSuccessSubscription!: Subscription;

  constructor(
    private apiService: PlanogramService,
    private router: Router,
    private commandService: CommandService,
    private blobService: AzureBlobStorageService,
    public dialog: MatDialog
  ) {
    super(initialState);
    this.commandFactory = new SkuCommandFactory();
    this.commandSuccessSubscription = commandService
      .commandSuccess()
      .subscribe((args) => {
        const action = args.action; //  Execute, Undo, or Redo
        // TODO: you might want to sync your data here
        if (action === 1 || action === 2) {
          if (args.result.collection) {
            this.setState({
              placed_sku_list: args.result.collection,
              is_update_highlight_sku_placeholder: true,
            });
          }
        }
      });
  }

  ngOnDestroy(): void {
    this.commandSuccessSubscription.unsubscribe();
  }

  setChartColorConfig(config: any) {
    this.setState({ ...config });
  }

  setPlanogramImage(image_url: string) {
    this.setState({ selected_planogram_image: image_url });
  }

  setPlanogramEdit(is_edit: boolean, id: number) {
    let edit_planogram_detail = {},
      selected_planogram_base_data = {};
    if (id !== -1) {
      edit_planogram_detail = { id: id };
    }
    if (is_edit) {
      this.getPlanogramSavedData(id);
    }

    this.setState({
      is_planogram_edit: is_edit,
      edit_planogram_detail: edit_planogram_detail,
      selected_planogram_base_data: selected_planogram_base_data,
    });
  }

  setCreateNewTemplateSuccessFlag(is_success: boolean) {
    if (!is_success) {
      this.setState({
        create_new_template_success: is_success,
        selected_cooler_template: {},
        selected_cooler_template_base_data: {},
      });
    } else {
      this.setState({ create_new_template_success: is_success });
    }
  }

  setSelectCoolerInfo(info: any) {
    if (info.id) {
      this.setState({ selected_cooler_template: info });
    } else {
      this.setState({
        selected_cooler_template: info,
        selected_cooler_template_base_data: {},
      });
    }
  }

  openEditAccessErrorPrompt(
    msg: string,
    next_event: boolean = false,
    is_navigate: boolean = false
  ) {
    this.setLoaderState(false);
    const dialogRef = this.dialog.open(ConfirmDialogModalComponent, {
      width: '500px',
      data: {
        header_msg: msg,
        header_msg_description: '',
        cancel_action: 'Cancel',
        ok_action: 'Ok',
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      this.setState({
        selected_planogram_base_data: JSON.parse(
          JSON.stringify(this.state.selected_planogram_base_data)
        ),
      });
      if (result) {
        if (is_navigate) {
          this.router.navigateByUrl(CONFIG.PATH.PLANOGRAMS, {
            state: { screen_index: 0, screen: 'planogram-list' },
          });
        }
        this.setState({ next_event_api_response: next_event });
      }
    });
  }

  setErrorState(msg: string, is_loader?: boolean) {
    if (msg === '') {
      this.setState({ error_obj: { is_error: false, error_msg: msg } });
    } else {
      msg =
        environment.environment_name !== dev_environment.environment_name
          ? 'Something went wrong.'
          : msg;
      this.setState({ error_obj: { is_error: true, error_msg: msg } });
    }
    if (is_loader && this.state.is_loader) {
      this.setLoaderState(false);
    }
  }

  setSKUInfo(sku: any) {
    this.setState({ sku_info: sku });
  }

  setNextEventAPIResponse(is_success: boolean) {
    this.setState({ next_event_api_response: is_success });
  }
  setLoaderState(is_loader: boolean) {
    let loading_count = this.state.loading_count;
    if (is_loader) {
      this.setState({ is_loader: is_loader, loading_count: loading_count + 1 });
    } else {
      loading_count = loading_count - 1;
      if (loading_count <= 0) {
        this.setState({ loading_count: 0, is_loader: is_loader });
      } else {
        this.setState({ loading_count: loading_count });
      }
    }
  }

  setPlanogramLoaderState(is_loader: boolean) {
    let planogram_loading_count = this.state.planogram_loading_count;
    if (is_loader) {
      this.setState({
        planogramLoader: is_loader,
        planogram_loading_count: planogram_loading_count + 1,
      });
    } else {
      planogram_loading_count = planogram_loading_count - 1;
      if (planogram_loading_count <= 0) {
        this.setState({
          planogram_loading_count: 0,
          planogramLoader: is_loader,
        });
      } else {
        this.setState({ planogram_loading_count: planogram_loading_count });
      }
    }
  }

  clearPlanogramNameError() {
    this.setState({ planogram_unique_name_error: '' });
  }

  clearTemplateNameError() {
    this.setState({ template_unique_name_error: '' });
  }

  getAdminCoolerData() {
    this.setLoaderState(true);
    this.apiService.getAdminCoolerData().subscribe({
      next: (res: any) => {
        if (res.code === 400) {
          this.setErrorState(res.data);
        } else {
          this.setState({
            adminCoolerData: res.data,
          });
          this.setLoaderState(false);
        }
      },
      error: (error) => {
        this.setErrorState(error);
      },
    });
  }

  uploadAdminCooler(info: any) {
    this.setLoaderState(true);
    this.apiService.uploadAdminCooler(info).subscribe({
      next: (res: any) => {
        if (res.code == 400) {
          this.dialog.open(AdminDialogComponent, {
            width: '500px',
            height: '200px',
            data: {
              header_msg: 'Error!',
              header_msg_description: 'error occured',
              cancel_action: 'Cancel',
              ok_action: 'Ok',
            },
          });
          this.setErrorState(res.data);
          this.setLoaderState(false);
        } else {
          this.setLoaderState(false);
          const dialogRefAdmin = this.dialog.open(AdminDialogComponent, {
            width: '500px',
            height: '200px',
            data: {
              header_msg: 'Success!',
              header_msg_description: res.data.msg,
              cancel_action: 'Cancel',
              ok_action: 'Ok',
            },
          });
          dialogRefAdmin.afterClosed().subscribe((result) => {
            // this.getAdminCoolerData();
            this.getAdminUpdateData();
            this.getAdminCoolerData();
          });
        }
      },
      error: (error) => {
        this.dialog.open(AdminDialogComponent, {
          width: '500px',
          height: '200px',
          data: {
            header_msg: 'Error!',
            header_msg_description: 'error occured',
            cancel_action: 'Cancel',
            ok_action: 'Ok',
          },
        });
        this.setLoaderState(false);
      },
    });
  }

  getAdminSkuData() {
    this.setLoaderState(true);
    this.apiService.getAdminSkuData().subscribe({
      next: (res: any) => {
        if (res.code === 400) {
          this.setErrorState(res.data);
        } else {
          this.setState({
            adminSkuData: res.data,
          });
          this.setLoaderState(false);
        }
      },
      error: (error) => {
        this.setErrorState(error);
      },
    });
  }

  uploadAdminSku(info: any) {
    this.setLoaderState(true);
    this.apiService.uploadAdminSku(info).subscribe({
      next: (res: any) => {
        if (res.code == 400) {
          this.dialog.open(AdminDialogComponent, {
            width: '500px',
            height: '200px',
            data: {
              header_msg: 'Error!',
              header_msg_description: 'error occured',
              cancel_action: 'Cancel',
              ok_action: 'Ok',
            },
          });
          this.setErrorState(res.data);
          this.setLoaderState(false);
          this.setErrorState(res.data);
        } else {
          this.setLoaderState(false);
          const dialogRefAdmin = this.dialog.open(AdminDialogComponent, {
            width: '500px',
            height: '200px',
            data: {
              header_msg: 'Success!',
              header_msg_description: res.data.msg,
              cancel_action: 'Cancel',
              ok_action: 'Ok',
            },
          });
          dialogRefAdmin.afterClosed().subscribe((result) => {
            //this.getAdminSkuData();
            this.getAdminUpdateData();
            this.getAdminSkuData();
          });
        }
      },
      error: (error) => {
        this.dialog.open(AdminDialogComponent, {
          width: '500px',
          height: '200px',
          data: {
            header_msg: 'Error!',
            header_msg_description: 'error occured',
            cancel_action: 'Cancel',
            ok_action: 'Ok',
          },
        });
        this.setLoaderState(false);
      },
    });
  }

  getUserInfo() {
    this.setLoaderState(true);
    this.apiService.getUserInfo().subscribe({
      next: (res: any) => {
        if (res.code === 400) {
          this.setErrorState(res.data);
        } else {
          this.setState({ user_info: res.data });
          this.setLoaderState(false);
        }
      },
      error: (error) => {
        this.setErrorState(error);
        this.setLoaderState(false);
      },
    });
  }

  saveBottlerQueries(payload: any) {
    this.setLoaderState(true);
    this.apiService.saveBottlerQueries(payload).subscribe({
      next: (res: any) => {
        if (res.code === 400) {
          this.setErrorState(res.data);
        } else {
          console.log('res save bottler', res);
          const dialogRef = this.dialog.open(ConfirmDialogModalComponent, {
            width: '500px',
            data: {
              header_msg: 'Success',
              header_msg_description: res.data.msg,
              ok_action: 'Ok',
            },
          });
          this.setLoaderState(false);
        }
      },
      error: (error) => {
        this.setErrorState(error);
      },
    });
  }

  getVersionedGuidelines(payload: any) {
    this.setLoaderState(true);
    this.apiService.getVersionedGuidelines(payload).subscribe({
      next: (res: any) => {
        if (res.code === 400) {
          this.setErrorState(res.data);
        } else {
          this.setState({
            versioned_guidelines: res.data,
          });
          this.setLoaderState(false);
        }
      },
      error: (error) => {
        this.setErrorState(error);
      },
    });
  }

  updateSkuPlacing(sku_placing_list: SKUModal[]) {
    this.executeCommand('update-properties', {
      target: 'placed_sku_list',
      collection: this.state.placed_sku_list,
      newValues: sku_placing_list,
    });
    this.setState({ placed_sku_list: sku_placing_list });
  }

  public executeCommand<K extends keyof SkuCommandMap>(
    key: string,
    commandData: SkuCommandMap[K]
  ) {
    const command = this.commandFactory.create(key, commandData);
    this.commandService.execute(key, command);
  }

  updateHighlightSkuPlaceholder(is_update: boolean) {
    this.setState({ is_update_highlight_sku_placeholder: is_update });
  }

  clearAutofillThroughputCalculation() {
    this.setState({ autofill_throughput_metrics: [] });
  }

  getAdminCoolerDropDownData() {
    this.setState({ adminCoolerDropDownData: admin_cooler_dropdown_data });
  }

  // API CALLS

  getPicosSKUsList(planogram_id: number, scenario: string) {
    this.apiService
      .getPicosSKUsList(planogram_id, 'picos', scenario)
      .subscribe({
        next: (res: any) => {
          if (res.code === 400) {
            this.setErrorState(res.data);
          } else {
            this.setState({
              picos_skus: sortSkusAccordingToBrandOrderAndSize(res),
            });
          }
        },
        error: (error) => {
          this.setErrorState(error);
        },
      });
  }

  getSKUsList(planogram_id: number, filter: string, scenario: string) {
    this.setState({ sku_list: [] });
    this.apiService.getSKUsList(planogram_id, filter, scenario).subscribe({
      next: (res: any) => {
        if (res.code === 400) {
          this.setErrorState(res.data);
        } else {
          let sku_list: any[] = [
            ...new Map(
              res.all_skus
                .concat(res.picos_recom)
                .map((item: any) => [item.sku_id, item])
            ).values(),
          ];
          this.setState({
            sku_list: sortSkusAccordingToBrandOrderAndSize(sku_list),
            brands: res.brand,
          });
        }
      },
      error: (error) => {
        this.setErrorState(error);
      },
    });
  }

  getSpecificSku(id: number) {
    this.apiService.getSpecificSku(id).subscribe({
      next: (res: any) => {
        this.setState({
          sku_info: res,
        });
      },
      error: (error) => {
        console.log('error :: ', error);
      },
    });
  }

  getSpecificPlanogram(id: number) {
    this.setState({ is_loader: true });
    this.apiService.getSpecificPlanogram(id).subscribe({
      next: (res: any) => {
        let cooler_config;
        if (res.template_meta) {
          cooler_config = {
            ...res.cooler_info,
            ...res.template_meta,
            vertical_orientation: res.vertical_orientation,
          };
        }
        let selected_planogram_base_data =
          Object.keys(this.state.selected_planogram_base_data).length > 0
            ? this.state.selected_planogram_base_data
            : res[0].planogram_meta;

        this.setState({
          cooler_config: cooler_config,
          throughput_metrics: [res.commercials],
          placed_sku_list: res.sku_info,
          is_update_highlight_sku_placeholder: true,
          selected_planogram_base_data: selected_planogram_base_data,
          is_loader: false,
        });
      },
      error: (error) => {
        this.setErrorState(error, true);
      },
    });
  }

  getSKUsListAndCoolerConfig(id: number, scenario: string) {
    if (id !== undefined) {
      this.setLoaderState(true);
      let required_keys: string[] = [
        'depth_of_sku',
        'dim_benchmark_clearance',
        'dim_height_clearance',
        'pack_type_filling_order',
        'fc_sku_filling_order',
        'ic_sku_filling_order',
        'brand_filling_order',
      ];
      this.getSpecificScreenData(required_keys);
      this.getAdditionalInfo(id);
      this.getRecommandations(id);
      this.setState({ sku_list: [] });
      forkJoin([
        this.apiService.getSpecificPlanogram(id),
        this.apiService.getSKUsList(id, 'all', scenario),
      ]).subscribe({
        next: (res: any) => {
          if (res[1].code === 400) {
            this.setErrorState(res[1].data, true);
          } else if (res[0].code === 400) {
            this.setErrorState(res[0].data, true);
          } else {
            let cooler_config;
            if (res[0].template_meta) {
              cooler_config = {
                ...res[0].cooler_info,
                ...res[0].template_meta,
                vertical_orientation: res[0].vertical_orientation,
              };
            }
            let selected_planogram_base_data =
              Object.keys(this.state.selected_planogram_base_data).length > 0
                ? this.state.selected_planogram_base_data
                : res[0].planogram_meta;
            let sku_list: any[] = [
              ...new Map(
                res[1].all_skus
                  .concat(res[1].picos_recom)
                  .map((item: any) => [item.sku_id, item])
              ).values(),
            ];

            this.setState({
              cooler_config: cooler_config,
              selected_planogram_base_data: selected_planogram_base_data,
              throughput_metrics: [res[0].commercials],
              placed_sku_list: res[0].sku_info,
              sku_list: sortSkusAccordingToBrandOrderAndSize(sku_list),
              brands: res[1].brand,
              is_update_highlight_sku_placeholder: true,
            });
            this.coolerAdjustment(res[0].sku_info);
            // this.countSKUPresentInCooler(res[0].sku_info);
          }
          this.setLoaderState(false);
        },
        error: (error) => {
          this.setErrorState(error, true);
        },
      });
    }
  }

  coolerAdjustment(data: any[]) {
    let sku_info_data = data;
    const shelfCount: Record<number, number> = {};
    let doors: number = 0;
    if (sku_info_data && sku_info_data.length !== 0) {
      sku_info_data.forEach((item: { shelf: number }) => {
        if (typeof item.shelf === 'number') {
          const shelf: number = item.shelf;
          shelfCount[shelf] = (shelfCount[shelf] || 0) + 1;
        }
      });
      interface ShelfData {
        cooler_shelf: number;
        no_of_facings: number;
      }
      let cooler_config_data: ShelfData[] | undefined;
      let shelves_data: Record<number, number> = {};
      this.cooler_config_info.subscribe((value) => {
        cooler_config_data = value.shelves;
        doors = value.no_of_doors;
      });

      if (cooler_config_data) {
        cooler_config_data.forEach((value) => {
          shelves_data[value.cooler_shelf] = value.no_of_facings;
        });
      }

      if (cooler_config_data && sku_info_data.length !== 0) {
        cooler_config_data.forEach((config) => {
          const shelf = config.cooler_shelf;
          if (shelfCount[shelf] !== undefined) {
            config.no_of_facings = Math.floor(shelfCount[shelf] / doors);
          }
        });
      }
    }
  }
  verifyPlanogramUniqueName(
    name: string,
    planogram_detail?: { planogram_id: number; planogram_name: string }
  ) {
    if (planogram_detail) {
      this.setLoaderState(true);
    }
    this.apiService.verifyPlanogramuniqueName(name).subscribe({
      next: (res: any) => {
        if (res.code === 400) {
          this.setState({ planogram_unique_name_error: res.data.msg });
          this.setLoaderState(false);
        } else {
          if (planogram_detail) {
            // this.copyPlanogram(planogram_detail, name);
            // this.setState({ close_copy_planogram_dialog: true });
          }
          this.setState({ planogram_unique_name_error: '' });
        }
      },
      error: (error) => {
        error =
          error.data && error.data.msg && typeof error.data.msg !== 'string'
            ? 'Please enter valid planogram name.'
            : error.data.msg;
        this.setState({ planogram_unique_name_error: error });
        this.setErrorState(error, planogram_detail !== null);
        this.setLoaderState(false);
      },
    });
  }

  getGuidelinesLibraryData() {
    this.setLoaderState(true);
    this.apiService.getGuidelinesLibraryData().subscribe({
      next: (res: any) => {
        this.setState({ guidelines_library_data: res.data });
        this.setLoaderState(false);
      },
      error: (error) => {
        const dialogRef = this.dialog.open(ConfirmDialogModalComponent, {
          width: '500px',
          data: {
            header_msg: 'Error',
            header_msg_description: error,
            ok_action: 'Ok',
          },
        });
        this.setLoaderState(false);
        this.setErrorState(error, true);
      },
    });
  }

  getBaseData() {
    this.setLoaderState(true);
    // this.setState({
    //         planogram_base_details: drop_down_data.data,
    //       });
    //       this.setLoaderState(false);
    this.apiService.getbasedata().subscribe({
      next: (res: any) => {
        this.setState({
          planogram_base_details: res.data,
        });
        this.setLoaderState(false);
      },
      error: (error) => {
        this.setErrorState(error, true);
      },
    });
  }

  async saveCustomPlanogram(cooler: string, info: any) {
    this.setLoaderState(true);
    let blob = await this.generateImage();
    this.apiService.saveCustomPlanogram(info).subscribe({
      next: (res: any) => {
        const dialogRef = this.dialog.open(ConfirmDialogModalComponent, {
          width: '500px',
          data: {
            header_msg: 'Success',
            header_msg_description: res.data,
            ok_action: 'Ok',
          },
        });
        dialogRef.afterClosed().subscribe(() => {
          // this.getReviewKpis({ id: info.id });
          // this.getLhsSkuList({ planogram_id: info.id });
          // this.fetchReviewSkuFacings({ planogram_id: info.id });
          this.savePlanogramSkuPlacement(
            info,
            true,
            cooler,
            info.id,
            blob,
            'custom planogram'
          );
        });

        this.setLoaderState(false);
      },
      error: (error) => {
        const dialogRef = this.dialog.open(ConfirmDialogModalComponent, {
          width: '500px',
          data: {
            header_msg: 'Success',
            header_msg_description: error,
            ok_action: 'Ok',
          },
        });
        dialogRef.afterClosed().subscribe(() => {
          this.getReviewKpis({ id: info.id });
          this.getLhsSkuList({ id: info.id });
          this.fetchReviewSkuFacings({ planogram_id: info.id });
        });
        this.setLoaderState(false);
        this.setErrorState(error, true);
      },
    });
  }

  getBaseNPDData(body_params: {
    channel: string;
    cooler_type: string;
    unit: string;
  }) {
    this.setLoaderState(true);

    this.apiService.getNPDdata(body_params).subscribe({
      next: (res: any) => {
        if (res.code === 400) {
          this.setErrorState(res);
        } else {
          let npd_data = res;
          this.setState({
            npd: npd_data,
          });
        }
        this.setLoaderState(false);
      },
      error: (error) => {
        this.setErrorState(error);
      },
    });
    this.setState({
      npd: npd_data,
    });
    this.setLoaderState(false);
  }

  getAllPlanograms(is_loader: boolean = true) {
    if (is_loader) {
      this.setLoaderState(is_loader);
    }
    this.apiService.getAllPlanograms().subscribe({
      next: (res: any) => {
        if (res.code === 400) {
          this.setErrorState(res.data, is_loader);
        } else {
          // let resp = JSON.parse(res)
          let saved_planogram = res.data;
          this.setState({
            saved_planogram: saved_planogram,
            // is_more_planograms: Object.keys(res).length === body_params.limit,
          });
        }
        if (is_loader) this.setLoaderState(false);
      },
      error: (error) => {
        this.setErrorState(error, is_loader);
      },
    });
  }

  getComparePlanograms(
    body_params: GetPlanogramBodyParams,
    is_loader: boolean = true
  ) {
    if (is_loader) {
      this.setLoaderState(is_loader);
    }
    this.apiService.getComparablePlanograms().subscribe({
      next: (res: any) => {
        if (res.code === 400) {
          this.setErrorState(res.data, is_loader);
        } else {
          this.setState({
            compare_planogram: { ...this.state.compare_planogram, ...res.data },
          });
        }
        if (is_loader) this.setLoaderState(false);
      },
      error: (error) => {
        this.setErrorState(error, is_loader);
      },
    });
  }

  savePlanogramBaseDetails(info: PlanogramBaseDetailModal, is_save: boolean) {
    this.setLoaderState(true);

    this.apiService.savePlanogramBaseDetails(info).subscribe({
      next: (res: any) => {
        this.setState({
          next_event_api_response: is_save,
          is_planogram_edit: true,
          edit_planogram_detail: res.data,
          reviewSkuList: res.data,
        });

        // if (is_save)
        //   this.router.navigateByUrl(CONFIG.PATH.PLANOGRAM + res.data.id, {
        //     state: { screen_index: 1 },
        //   });
        // else
        //this.router.navigateByUrl(CONFIG.PATH.PLANOGRAMS);
        this.router.navigateByUrl(CONFIG.PATH.PLANOGRAM + res.data.id, {
          state: { screen_index: 1 },
        });
        this.setLoaderState(false);
      },
      error: (error) => {
        this.setErrorState(error, true);
      },
    });
  }

  editPlanogramBaseDetails(info: PlanogramBaseDetailModal, is_save: boolean) {
    this.setLoaderState(true);
    this.apiService.editPlanogramBaseDetails(info).subscribe({
      next: (res: any) => {
        this.setState({
          next_event_api_response: is_save,
          is_planogram_edit: true,
          edit_planogram_detail: res.data,
          reviewSkuList: res.data,
        });
        this.setLoaderState(false);
      },
      error: (error) => {
        error = error;
        console.log('error======>', error);
        const dialogRef = this.dialog.open(ConfirmDialogModalComponent, {
          width: '500px',
          data: {
            header_msg: 'Error',
            header_msg_description: error.data.msg,
            ok_action: 'Ok',
          },
        });
        dialogRef.afterClosed().subscribe(() => {
          this.getAllPlanograms(false);
        });
        if (error.pop_up_error) {
          //this.openEditAccessErrorPrompt(error.msg);
        } else {
          this.setErrorState(error, true);
        }
      },
    });
  }

  getReviewKpis(info: any) {
    this.setLoaderState(true);
    this.apiService.getReviewKpis(info).subscribe({
      next: (res: any) => {
        this.setState({
          reviewKpis: res.data,
        });
        this.setLoaderState(false);
      },
      error: (error) => {
        error = error;
        console.log('error======>', error);
        const dialogRef = this.dialog.open(ConfirmDialogModalComponent, {
          width: '500px',
          data: {
            header_msg: 'Error',
            header_msg_description: 'Error occured in review kpis api',
            ok_action: 'Ok',
          },
        });
        this.setLoaderState(false);
        dialogRef.afterClosed().subscribe(() => {
          this.getAllPlanograms(false);
        });
        if (error.pop_up_error) {
          //this.openEditAccessErrorPrompt(error.msg);
        } else {
          this.setErrorState(error, true);
        }
      },
    });
  }

  updatePlanogram(payload: any) {
    this.setLoaderState(true);
    this.apiService.updatePlanogram(payload).subscribe({
      next: (res: any) => {
        console.log('res', res);
        this.router.navigateByUrl(CONFIG.PATH.PLANOGRAM + res.data.id, {
          state: { screen_index: 2 },
        });
        this.setLoaderState(false);
      },
      error: (error) => {
        error = error;
        console.log('error======>', error);
      },
    });
  }

  setUpSaveAsDraftPlanogramBaseDetails(
    info: PlanogramBaseDetailModal,
    is_save: boolean
  ) {
    this.setLoaderState(true);
    this.apiService.setUpSaveAsDraftPlanogramBaseDetails(info).subscribe({
      next: (res: any) => {
        this.setState({
          next_event_api_response: is_save,
          is_planogram_edit: true,
          edit_planogram_detail: res.data,
        });
        this.setLoaderState(false);
        this.router.navigateByUrl(CONFIG.PATH.PLANOGRAMS, {
          state: { screen_index: 0, screen: 'planogram-list' },
        });
      },
      error: (error) => {
        error = error;
        console.log('error', error);
        this.setLoaderState(false);
        const dialogRef = this.dialog.open(ConfirmDialogModalComponent, {
          width: '500px',
          data: {
            header_msg: 'Error',
            header_msg_description: error,
            ok_action: 'Ok',
          },
        });
        dialogRef.afterClosed().subscribe(() => {
          this.getAllPlanograms(false);
        });
      },
    });
  }

  setImagesToReviewList(sku_face_category: string, imgUri: string) {
    this.state.reviewScreenSkuImages[sku_face_category] = imgUri
  }

  async processFacingsData(facingsData: any): Promise<any> {
    const listData: any = { facings_data: {} };
    const imagesCache: any = {};
    let imageCounter = 0;
    try {
      for (const cooler of Object.keys(facingsData)) {
        const { dimensions, facings, shelves } = facingsData[cooler];
        const facings_length = facings.length;
        this.setPlanogramLoaderState(true);
        console.log('facings_length', facings_length);
        const facingsWithImages = await Promise.all(
          facings.map(async (facing: any, index: number) => {
            const skuFaceCategory = `${facing.sku_face}_${facing.category}`;
            if (!imagesCache[skuFaceCategory]) {
              imagesCache[skuFaceCategory] = '';
              try {
                imageCounter++;
                this.apiService
                  .fetchImageFromBlob(facing.sku_image_uri)
                  .subscribe({
                    next: (res: any) => {
                      const imageUrl = URL.createObjectURL(res);
                      facing.blob_img_uri = imageUrl;
                      if (imageUrl) {
                        imagesCache[skuFaceCategory] = imageUrl;
                      }
                      if (index == facings_length - 1) {
                        this.setPlanogramLoaderState(false);
                      }
                    },
                    error: (error) => {
                      imagesCache[skuFaceCategory] = null;
                      console.log('Error fetching image:', error);
                    },
                  });
              } catch (error) {
                console.error(
                  `Error fetching image for SKU: ${facing.sku_image_uri}`,
                  error
                );
              }
            }

            return facing;
          })
        );
        console.log('facingsWithImages==========>', facingsWithImages);
        listData.facings_data[cooler] = {
          dimensions,
          facings: facingsWithImages,
          shelves,
        };
      }
      await Promise.all(imagesCache);
    } catch (error) {
      console.error('Error processing facings data:', error);
    } finally {
      // let totalImagesToBeFetched = Object.keys(imagesCache);
      // if (
      //  imageCounter==totalImagesToBeFetched.length-1
      // ) {
      //    this.setPlanogramLoaderState(false);
      // }
      // Set loader state to false after processing
      //this.setPlanogramLoaderState(false);
    }
    return { listData, imagesCache };
  }

  fetchReviewSkuFacings(info: any) {
    this.setLoaderState(true);
    this.apiService.getReviewSkuFacings(info).subscribe({
      next: async (res: any) => {
        const { facings_data } = res.data;
        const { listData, imagesCache } = await this.processFacingsData(
          facings_data
        );

        this.setState({
          reviewScreenSkuImages: imagesCache,
          reviewSkuList: listData,
        });
        this.setLoaderState(false);
      },
      error: (error) => {
        error = JSON.parse(error);
        if (error.pop_up_error) {
          this.openEditAccessErrorPrompt(error.msg);
        } else {
          this.setErrorState(error, true);
        }
      },
    });
  }

  deletePlanogram(id: number) {
    this.setLoaderState(true);
    this.apiService.deletePlanogram(id).subscribe({
      next: (res: any) => {
        // if (res.delete_flag) {
        //   let planogram_limit = document.cookie.split('planogram_limit=')[1]
        //     ? Number(document.cookie.split('planogram_limit=')[1])
        //     : 0;
        //   this.getAllPlanograms(

        //     false
        //   );
        // }
        if (res.data && res.data.msg.toLowerCase().includes('succes')) {
          this.setLoaderState(false);
          const dialogRef = this.dialog.open(ConfirmDialogModalComponent, {
            width: '500px',
            data: {
              header_msg: 'Success',
              header_msg_description: res.data.msg,
              ok_action: 'Ok',
            },
          });
          dialogRef.afterClosed().subscribe(() => {
            // this.getAllPlanograms(false);
            // this.setLoaderState(true);
            this.fetchCustomPlanograms();
          });
        } else {
          this.setState({
            error_obj: { is_error: true, error_msg: res.data.msg },
          });
        }
      },
      error: (error) => {
        this.setLoaderState(false);
        this.dialog.open(ConfirmDialogModalComponent, {
          width: '500px',
          data: {
            header_msg: 'Error',
            header_msg_description: error.data.msg,
            ok_action: 'Ok',
          },
        });
        //this.setErrorState(error);
      },
    });
  }

  getPlanogramSavedData(id: number) {
    this.setLoaderState(true);
    this.apiService.getPlanogramSavedData(id).subscribe({
      next: (res: any) => {
        this.setState({ selected_planogram_base_data: res.data });
        this.setLoaderState(false);
      },
      error: (error) => {
        this.setErrorState(error);
      },
    });
  }

  setLockedGuidelines(payload: any) {
    this.setLoaderState(true);
    this.apiService.setLockedGuidelines(payload).subscribe({
      next: (res: any) => {
        const dialogRef = this.dialog.open(ConfirmDialogModalComponent, {
          width: '500px',
          data: {
            header_msg: 'Sucess!',
            header_msg_description: res.data.msg,
            cancel_action: 'Cancel',
            ok_action: 'Ok',
          },
        });
        dialogRef.afterClosed().subscribe((result) => {
          this.getGuidelinesLibraryData();
        });
        // this.setState({ selected_planogram_base_data: res.data });
        this.setLoaderState(false);
      },
      error: (error) => {
        this.setErrorState(error);
      },
    });
  }

  getCoolerTemplateBaseData(id: number) {
    this.apiService.getCoolerTemplateBaseData(id).subscribe({
      next: (res: any) => {
        this.setState({ cooler_template_base_data: res });
      },
      error: (error) => {
        this.setErrorState(error);
      },
    });
  }

  createPlanogramVersion(payload: any) {
    this.setLoaderState(true);
    this.apiService.createPlanogramVersion(payload).subscribe({
      next: (res: any) => {
        this.setState({ adminSkuData: res.data });
        this.setLoaderState(false);
        this.router.navigateByUrl(
          CONFIG.PATH.ADMIN_SETUP_SCREEN + res.data.id,
          {
            state: {
              screen_index: 2,
              planogram_id: res.data.id,
              from: 'guidelines_input',
            },
          }
        );
        return res.data.id;
      },
      error: (error) => {
        this.setErrorState(error);
      },
    });
  }

  getSpecificScreenData(keys: string[]) {
    this.apiService.getSpecificScreenData(keys).subscribe({
      next: (res: any) => {
        this.setState({ specific_screen_data: res });
      },
      error: (error) => {
        this.setErrorState(error);
      },
    });
  }

  verifyTemplateUniqueName(name: string, id: string) {
    this.apiService.verifyTemplateUniqueName(name, id).subscribe({
      next: (res: any) => {
        if (res.code === 400) {
          this.setState({ template_unique_name_error: res.data });
        } else {
          this.setState({ template_unique_name_error: '' });
        }
      },
      error: (error) => {
        error =
          typeof error !== 'string'
            ? 'Please enter valid planogram name.'
            : error;
        this.setErrorState(error);
      },
    });
  }

  saveCoolerTemplate(info: any) {
    this.apiService.saveCoolerTemplate(info).subscribe({
      next: (res: any) => {
        if (res.code === 400) {
          this.setErrorState(res.data);
        } else {
          this.setState({
            create_new_template_success: true,
            selected_cooler_template: res,
          });
        }
      },
      error: (error) => {
        this.setErrorState(error);
      },
    });
  }

  editCoolerTemplate(info: any) {
    this.apiService.editCoolerTemplate(info).subscribe({
      next: (res: any) => {
        if (res.code === 400) {
          this.setErrorState(res.data);
        } else {
          this.setState({
            selected_cooler_template: res,
            create_new_template_success: true,
          });
        }
      },
      error: (error) => {
        this.setErrorState(error);
      },
    });
  }

  getSavedCoolerTemplateDataAndTemplateBaseData(
    planogram_id: number,
    id: number
  ) {
    this.setLoaderState(true);
    forkJoin([
      this.apiService.getSavedCoolerTemplateData(id),
      this.apiService.getCoolerTemplateBaseData(planogram_id),
    ]).subscribe({
      next: (res: any) => {
        this.setState({
          selected_cooler_template_base_data: res[0],
          cooler_template_base_data: res[1],
        });
        this.setLoaderState(false);
      },
      error: (error) => {
        this.setErrorState(error, true);
      },
    });
  }

  getSavedCoolerTemplateData(id: number) {
    this.apiService.getSavedCoolerTemplateData(id).subscribe({
      next: (res: any) => {
        this.setState({ selected_cooler_template_base_data: res });
      },
      error: (error) => {
        this.setErrorState(error);
      },
    });
  }

  getTemplates(id: number) {
    this.setLoaderState(true);
    this.apiService.getAllTemplates(id).subscribe({
      next: (res: any) => {
        if (res.code === 400) {
          this.setErrorState(res.data);
        } else {
          this.setState({ saved_templates: res });
        }
        this.setLoaderState(false);
      },
      error: (error) => {
        this.setErrorState(error, true);
      },
    });
  }

  saveTemplateToPlanogram(info: any, is_save: boolean) {
    this.setLoaderState(true);
    this.apiService.saveTemplateToPlanogram(info).subscribe({
      next: (_res: any) => {
        if (is_save) this.setState({ next_event_api_response: is_save });
        else
          this.router.navigateByUrl(CONFIG.PATH.PLANOGRAMS, {
            state: { screen_index: 0, screen: 'planogram-list' },
          });

        this.setLoaderState(false);
      },
      error: (error) => {
        error = JSON.parse(error);
        if (error.pop_up_error) {
          this.openEditAccessErrorPrompt(error.msg);
        } else {
          this.setErrorState(error, true);
        }
      },
    });
  }

  editCoolerFacing(info: any) {
    this.apiService.editCoolerFacing(info).subscribe({
      error: (error) => {
        error = JSON.parse(error);
        if (error.pop_up_error) {
          this.openEditAccessErrorPrompt(error.msg, false);
        } else {
          this.setErrorState(error, true);
          console.log('error :: ', error);
        }
      },
    });
  }

  async savePlanogramSkuPlacement(
    info: any,
    is_save: boolean,
    // name: string,
    cooler: string,
    id: any,
    blob_present?: any,
    from?: string
  ) {
    console.log('from', from);
    this.setLoaderState(true);
    // if (!blob_present) {
    //   blob = await this.generateImage();
    // } else {
    //   blob = blob_present;
    // }
    let  blob = await this.generateImage();
    console.log('blob', blob);
    let user_name = localStorage.getItem('user_name') || '';
    if (is_save) {
      let planogram_image_uri = getPlanogramImageUrl(
        id,
        // name,
        user_name,
        cooler,
        false
      );
      info.uri_link = planogram_image_uri;
    }
    if (!!is_save) {
      if (blob) {
        // this.blobService.uploadImage(
        //   blob,
        //   `PlanogramImages/${id}_${user_name}_${cooler}_cooler_image`,
        //   (res: any) => {
        //     console.log('res image', res);

        //     this.apiService.publishPlanogram(info).subscribe({
        //       next: (_res: any) => {
        //         if (from == 'admin') {
        //           this.router.navigateByUrl(
        //             CONFIG.PATH.ADMIN_UPDATE_GUIDELINES
        //           );
        //         }
        //         if (from == 'custom planogram') {
        //           this.router.navigateByUrl(
        //             CONFIG.PATH.CUSTOM_PLANOGRAM_LIBRARY
        //           );
        //         } else this.router.navigateByUrl(CONFIG.PATH.LANDING_PAGE);
        //         this.setState({
        //           next_event_api_response: is_save,
        //           analysisKPIs: _res.data,
        //         });
        //         this.setLoaderState(false);
        //       },
        //       error: (error) => {
        //         console.log('error', error);
        //       },
        //     });
        //   }
        // );
        const formData = new FormData();
        formData.append('file', blob);
        formData.append('id', id); // Attach a title field
        formData.append('user_name', user_name);
        formData.append('cooler', cooler);
        this.apiService.uploadImage(formData).subscribe({
          next: (res: any) => {
            this.apiService.publishPlanogram(info).subscribe({
              next: (_res: any) => {
                if (from == 'admin') {
                  this.router.navigateByUrl(
                    CONFIG.PATH.ADMIN_UPDATE_GUIDELINES
                  );
                }
                if (from == 'custom planogram') {
                  this.router.navigateByUrl(
                    CONFIG.PATH.CUSTOM_PLANOGRAM_LIBRARY
                  );
                } else this.router.navigateByUrl(CONFIG.PATH.LANDING_PAGE);
                this.setState({
                  next_event_api_response: is_save,
                  analysisKPIs: _res.data,
                });
                this.setLoaderState(false);
              },
              error: (error) => {
                console.log('error', error);
              },
            });
          },
          error: (error) => {
            console.log('error======>', error);
          },
        });
      }
    } else {
      this.apiService.publishPlanogram(info).subscribe({
        next: (_res: any) => {
          this.setState({
            next_event_api_response: is_save,
            analysisKPIs: _res.data,
          });
          this.setLoaderState(false);
        },
        error: (error) => {
          console.log('error', error);
          // error = JSON.parse(error);
          // if (error.pop_up_error) {
          //   this.openEditAccessErrorPrompt(error.msg);
          // } else {
          //   this.setErrorState(error, true);
          //   console.log('error :: ', error);
          // }
        },
      });
    }
  }
  generateImage = async () => {
    let cooler_node = document.getElementById('image-capture-container');
    if (cooler_node) {
      return htmlToImage
        .toBlob(cooler_node, {
          width: cooler_node.scrollWidth,
          height: cooler_node.scrollHeight,
          canvasWidth: cooler_node.scrollWidth,
          canvasHeight: cooler_node.scrollHeight,
          pixelRatio: 1,
          skipAutoScale: false,
          quality: 1,
        })
        .then(function (blob) {
          return blob;
        })
        .catch(function (error: any) {
          console.error('oops, something went wrong!', error);
        });
    }
  };

  generateImageAsPNG = async () => {
    this.setLoaderState(true);
    const coolerNode = document.getElementById('image-capture-container');
    const skuEditElements = coolerNode?.querySelectorAll('.sku-edit');
    if (skuEditElements) {
      skuEditElements.forEach((element) => {
        (element as HTMLElement).style.display = 'none';
      });
    }
    if (coolerNode) {
      try {
        const dataUrl = await htmlToImage.toPng(coolerNode, {
          width: coolerNode.scrollWidth,
          height: coolerNode.scrollHeight,
          canvasWidth: coolerNode.scrollWidth,
          canvasHeight: coolerNode.scrollHeight,
          pixelRatio: 1,
          skipAutoScale: false,
          quality: 1,
        });
        const link = document.createElement('a');
        link.download = 'cooler-image.png';
        link.href = dataUrl;
        link.click();
      } catch (error) {
        console.error('Oops, something went wrong!', error);
      } finally {
        if (skuEditElements) {
          skuEditElements.forEach((element) => {
            (element as HTMLElement).style.display = '';
          });
        }

        this.setLoaderState(false);
      }
    } else {
      this.setLoaderState(false);
    }
  };

  getCoolerInfo(payload: any) {
    //   let res={
    //         "name": "45 Caser",
    //         "dimension": {
    //             "45 Caser (30)": "590 x 945 x 1631",
    //             "45 Caser (15.5)": "590 x 503 x 1631"
    //         },
    //         "shelves": {
    //             "45 Caser (30)": 5,
    //             "45 Caser (15.5)": 6
    //         },
    //         "product facing": {
    //             "45 Caser (30)": 61,
    //             "45 Caser (15.5)": 39
    //         },
    //         "shelf config": {
    //             "45 Caser (30)": {
    //                 "SS": 2,
    //                 "OTG": 1,
    //                 "HPET/LPET": 2
    //             },
    //             "45 Caser (15.5)": {
    //                 "SS": 2,
    //                 "OTG": 2,
    //                 "HPET/LPET": 2
    //             }
    //         },
    //         "image url": {
    //             "45 Caser (30)": "https://cciplanogramstorage.blob.core.windows.net/development/CoolerImages/30 Caser.png",
    //             "45 Caser (15.5)": "https://cciplanogramstorage.blob.core.windows.net/development/CoolerImages/15.5 Caser.png"
    //         }
    // }
    this.setLoaderState(true);
    this.apiService.getCoolerInfo(payload).subscribe({
      next: (res: any) => {
        this.setState({ coolerInfo: res.data });
        this.setLoaderState(false);
      },
      error: (error) => {
        console.log('error :: ', error);
        this.setErrorState(error);
      },
    });
  }

  getGuidelinesInput(payload: any) {
    this.setLoaderState(true);
    this.apiService.getGuidelinesInput(payload).subscribe({
      next: (res: any) => {
        this.setState({ guidelinesInput: res.data });
        this.setLoaderState(false);
      },
      error: (error) => {
        const dialogRefAdmin = this.dialog.open(AdminDialogComponent, {
          width: '500px',
          height: '200px',
          data: {
            header_msg: 'Error!',
            header_msg_description: 'Error occured in gudelines input api',
            cancel_action: 'Cancel',
            ok_action: 'Ok',
          },
        });
        this.setLoaderState(false);
        console.log('error :: ', error);
        this.setErrorState(error);
      },
    });
  }

  publishPlanogram(info: any) {
    this.apiService.publishPlanogram(info).subscribe({
      next: (_res: any) => {
        this.router.navigateByUrl(CONFIG.PATH.PLANOGRAMS, {
          state: { screen_index: 0, screen: 'planogram-list' },
        });
      },
      error: (error) => {
        error = JSON.parse(error);
        if (error.pop_up_error) {
          this.openEditAccessErrorPrompt(error.msg, false, false);
        } else {
          this.setErrorState(error, true);
          console.log('error :: ', error);
        }
      },
    });
  }

  comparePlanogram(ids: number[], is_from_publish: boolean = false) {
    this.setLoaderState(true);
    this.apiService.comparePlanogram(ids).subscribe({
      next: (res: any) => {
        this.setState({ compare_planogram_data: res.data });
        this.router.navigateByUrl(CONFIG.PATH.COMPAREPLANOGRAM, {
          state: { ids: ids, is_from_publish: is_from_publish },
        });
        this.setLoaderState(false);
      },
      error: (error) => {
        console.log('error :: ', error);
        this.setErrorState(error, true);
      },
    });
  }

  getAdminBaseData(payload: any) {
    this.setLoaderState(true);
    this.apiService.getAdminBaseData(payload).subscribe({
      next: (res_: any) => {
        this.setState({ adminBaseData: res_.data });
        this.setLoaderState(false);
      },
      error: (error) => {
        console.log('error :: ', error);
        this.setErrorState(error);
      },
    });
  }

  getAdminUpdatedBaseData(payload: any): Observable<any> {
    this.setLoaderState(true);

    return this.apiService.getAdminBaseData(payload).pipe(
      tap((res_: any) => {
        this.setState({ adminBaseData: res_.data });
      }),
      catchError((error) => {
        console.log('error :: ', error);
        this.setErrorState(error);
        return throwError(error);
      }),
      finalize(() => {
        this.setLoaderState(false);
      })
    );
  }

  incorporate_npds(payload: any) {
    this.setLoaderState(true);
    this.apiService.incorporate_npds(payload).subscribe({
      next: async (res_: any) => {
        console.log('create res========>', res_);
        this.setLoaderState(false);
        const { listData, imagesCache }= await this.processFacingsData(res_.data.facings_data);
        this.setState({
          reviewScreenSkuImages: imagesCache,
          reviewSkuList: listData,
        });
        this.router.navigateByUrl(CONFIG.PATH.PLANOGRAM + res_.data.id, {
          state: { screen_index: 2 },
        });
        console.log('incorporate_npds', this.router);
      },
      error: (error) => {
        const dialogRefAdmin = this.dialog.open(AdminDialogComponent, {
          width: '500px',
          height: '200px',
          data: {
            header_msg: 'Error!',
            header_msg_description: 'Error occured in incorporate npds api',
            cancel_action: 'Cancel',
            ok_action: 'Ok',
          },
        });
        this.setLoaderState(false);
        console.log('error :: ', error);
        this.setErrorState(error);
      },
    });
  }

  getAdminBaseDataBasedOnTypeOfFile(payload: any) {
    this.setLoaderState(true);
    this.apiService.getAdminBaseData(payload).subscribe({
      next: (res_: any) => {
        this.setState({ adminBaseDataBasedonFileType: res_.data });
        this.setLoaderState(false);
      },
      error: (error) => {
        console.log('error :: ', error);
        this.setErrorState(error);
      },
    });
  }

  createBasePlanogram(payload: any) {
    this.setLoaderState(true);
    this.apiService.createBasePlanogram(payload).subscribe({
      next: (res_: any) => {
        console.log('create res========>', res_);
        this.setLoaderState(false),
          this.router.navigateByUrl(
            CONFIG.PATH.PLANOGRAM + res_.data.planogram_id,
            {
              state: { screen_index: 1, from: 'library' },
            }
          );
      },
      error: (error) => {
        const dialogRefAdmin = this.dialog.open(AdminDialogComponent, {
          width: '500px',
          height: '200px',
          data: {
            header_msg: 'Error!',
            header_msg_description:
              'Error occured in create base planogram api',
            cancel_action: 'Cancel',
            ok_action: 'Ok',
          },
        });
        this.setLoaderState(false);
        console.log('error :: ', error);
        this.setErrorState(error);
      },
    });
  }

  uploadAdminBaseData(payload: any) {
    this.setLoaderState(true);
    this.apiService.uploadAdminBaseData(payload).subscribe({
      next: (res: any) => {
        if (res.code == 400) {
          this.dialog.open(AdminDialogComponent, {
            width: '500px',
            height: '200px',
            data: {
              header_msg: 'Error!',
              header_msg_description: 'error occured',
              cancel_action: 'Cancel',
              ok_action: 'Ok',
            },
          });
          this.setLoaderState(false);
        } else {
          this.setLoaderState(false);
          console.log('res data', res);
          const dialogRefAdmin = this.dialog.open(AdminDialogComponent, {
            width: '500px',
            height: '200px',
            data: {
              header_msg: 'Success!',
              header_msg_description: res.data.msg,
              cancel_action: 'Cancel',
              ok_action: 'Ok',
            },
          });
          // if (payload.type === 'guidelines') {
          dialogRefAdmin.afterClosed().subscribe((result) => {
            //this.getAdminBaseData({ type: 'guidelines' });
            this.getAdminUpdateData();
            //this.getAdminBaseData({ type: payload.type })
          });
          // }
        }
      },
      error: (error) => {
        this.dialog.open(AdminDialogComponent, {
          width: '500px',
          height: '200px',
          data: {
            header_msg: 'Error!',
            header_msg_description: 'error occured',
            cancel_action: 'Cancel',
            ok_action: 'Ok',
          },
        });
        this.setLoaderState(false);
      },
    });
  }

  createCustomPlanogram(payload: any) {
    this.setLoaderState(true);
    this.apiService.createCustomPlanogram(payload).subscribe({
      next: (res: any) => {
        if (res.code === 400) {
          this.setErrorState(res.data);
        } else {
          this.setState({ reviewKpis: res.data });
          localStorage.setItem('admin_custom_plano_stepper_index', '1');
          this.router.navigateByUrl(
            CONFIG.PATH.ADMIN_CUSTOM_PLANOGRAM + res.data.id,
            {
              state: { screen_index: 1, planogram_id: res.data.id },
            }
          );
        }
        this.setLoaderState(false);
      },
      error: (error) => {
        this.setErrorState(error, true);
      },
    });
  }

  fetchCustomPlanograms() {
    this.setLoaderState(true);
    this.apiService.fetchCustomPlanograms({}).subscribe({
      next: (res: any) => {
        if (res.code === 400) {
          this.setErrorState(res.data);
        } else {
          this.setState({ customPlanogramLibrary: res.data });
        }
        this.setLoaderState(false);
      },
      error: (error) => {
        this.setErrorState(error, true);
      },
    });
  }
  updateMinimumFacings(id: number, facings: number) {
    this.setLoaderState(true);
    this.apiService
      .updateMinimumFacings({ user_id: id, minimum_facings: facings })
      .subscribe({
        next: (res: any) => {
          if (res.code === 400) {
            this.setErrorState(res.data);
          } else {
            // this.setState({ customPlanogramLibrary: res.data });
            this.setErrorState(res.data.msg);
          }
          this.setLoaderState(false);
        },
        error: (error) => {
          this.setErrorState(error, true);
        },
      });
  }

  getPlanogramDataAndCoolerConfig(id: number) {
    this.setLoaderState(true);
    forkJoin([
      this.apiService.getSpecificPlanogram(id),
      this.apiService.comparePlanogram([id]),
    ]).subscribe({
      next: (res: any) => {
        let cooler_config;
        if (res[0].template_meta) {
          cooler_config = {
            ...res[0].cooler_info,
            ...res[0].template_meta,
            vertical_orientation: res[0].vertical_orientation,
          };
        }
        let selected_planogram_base_data =
          Object.keys(this.state.selected_planogram_base_data).length > 0
            ? this.state.selected_planogram_base_data
            : res[0].planogram_meta;
        let planogram_data = res[1];
        this.setState({
          cooler_config: cooler_config,
          selected_planogram_base_data: selected_planogram_base_data,
          throughput_metrics: [res[0].commercials],
          placed_sku_list: res[0].sku_info,
          compare_planogram_data: planogram_data,
          is_update_highlight_sku_placeholder: true,
        });
        this.setLoaderState(false);
      },
      error: (error) => {
        console.log('error :: ', error);
        this.setErrorState(error, true);
      },
    });
  }

  autofillSkus(planogram_id: number) {
    this.setLoaderState(true);
    this.apiService.autoFillSKUS(planogram_id).subscribe({
      next: (res: any) => {
        if (res.code === 400) {
          this.setErrorState(res.data, true);
        } else {
          this.coolerAdjustment(res.sku_info);
          // this.countSKUPresentInCooler(res.sku_info);
          this.setState({
            placed_sku_list: res.sku_info,
            autofill_throughput_metrics: [res.commercials],
            throughput_metrics: [res.commercials],
            note_for_blank_sku: res.Note,
          });
        }
        this.setLoaderState(false);
      },
      error: (error) => {
        console.log('error :: ', error);
        this.setErrorState(error, true);
      },
    });
  }
  getRecommandations(id: number) {
    this.apiService.getRecommandations(id).subscribe({
      next: (res: any) => {
        this.setState({ recommandations: res });
      },
      error: (error) => {
        console.log('error :: ', error);
        this.setErrorState(error);
      },
    });
  }

  getAdditionalInfo(planogram_id: number) {
    this.apiService.getAdditionalInfo(planogram_id).subscribe({
      next: (res_: any) => {
        this.setState({ planogram_metrics: res_ });
      },
      error: (error) => {
        console.log('error :: ', error);
        this.setErrorState(error);
      },
    });
  }

  getThroughputCalculation(info: any) {
    this.apiService.calculateThroughput(info).subscribe({
      next: (res: any) => {
        this.setState({ throughput_metrics: res });
      },
      error: (error) => {
        this.setState({ throughput_metrics: [] });
        console.log('error :: ', error);
        this.setErrorState(error);
      },
    });
  }

  verticalAutofillSkus(planogram_id: number, block_config: any) {
    this.setLoaderState(true);
    this.apiService.verticalAutoFillSKUS(planogram_id, block_config).subscribe({
      next: (v_res: any) => {
        if (v_res.code === 400) {
          this.setErrorState(v_res.data, true);
        } else {
          this.coolerAdjustment(v_res.sku_info);
          // this.countSKUPresentInCooler(v_res.sku_info);
          this.setState({
            placed_sku_list: v_res.sku_info,
            autofill_throughput_metrics: [v_res.commercials],
            throughput_metrics: [v_res.commercials],
            note_for_blank_sku: v_res.Note,
          });
        }
        this.setLoaderState(false);
      },
      error: (error) => {
        console.log('error :: ', error);
        this.setErrorState(error, true);
      },
    });
  }

  getLhsSkuList(info: any) {
    this.apiService.getLhsSKUsList(info).subscribe({
      next: (res: any) => {
        this.setState({ reviewLhsSkuList: res.data });
      },
      error: (error) => {
        console.log('error :: ', error);
        this.setErrorState(error, true);
      },
    });
  }

  // copyPlanogram(
  //   existing_planogram: { planogram_id: number; planogram_name: string },
  //   planogram_name: string
  // ) {
  //   this.apiService
  //     .copyPlanogram(existing_planogram.planogram_id, planogram_name)
  //     .subscribe({
  //       next: (res: any) => {
  //         if (res.code === 400) {
  //           this.setErrorState(res.data, true);
  //         } else {
  //           // let source_url = getPlanogramImageUrl(
  //           //   existing_planogram.planogram_id,
  //           //   existing_planogram.planogram_name,
  //           //   false
  //           // );
  //           let source_url = '';
  //           let destination_file_name = `${res.cloned_planogram_id}/latest/${environment.environment_name}/${planogram_name}-cooler-image`;
  //           let source_file_name = `${existing_planogram.planogram_id}/latest/${environment.environment_name}/${existing_planogram.planogram_name}-cooler-image`;
  //           this.blobService.copyImage(
  //             source_url,
  //             destination_file_name,
  //             source_file_name,
  //             (_res: any) => {}
  //           );
  //           let planogram_limit = document.cookie.split('planogram_limit=')[1]
  //             ? Number(document.cookie.split('planogram_limit=')[1])
  //             : 0;
  //           this.getAllPlanograms(false);
  //           this.setLoaderState(false);
  //         }
  //       },
  //       error: (error) => {
  //         console.log('error :: ', error);
  //         this.setErrorState(error, true);
  //       },
  //     });
  // }

  getSavedPlanogramData(payload: any) {
    this.setLoaderState(true);
    this.apiService.getSavedPlanogramInfo(payload).subscribe({
      next: (res: any) => {
        if (res.code === 400) {
          this.setErrorState(res.data);
        } else {
          this.setState({
            saved_planogram_landing_page_info: res.data,
          });
          this.setLoaderState(false);
        }
      },
      error: (error) => {
        this.setErrorState(error);
        this.setLoaderState(false);
      },
    });
  }

  getAdminUpdateData() {
    this.setLoaderState(true);
    this.apiService.getAdminUpdateData().subscribe({
      next: (res: any) => {
        console.log('res', res);
        if (res.code === 400) {
          this.setErrorState(res.data);
        } else {
          this.setState({
            adminUpdateData: res.data,
          });
          this.setLoaderState(false);
        }
      },
      error: (error) => {
        this.setErrorState(error);
      },
    });
  }

  getAdminUserAnalyticsData() {
    this.setLoaderState(true);
    this.apiService.getAdminUserAnalyticsData().subscribe({
      next: (res: any) => {
        if (res.code === 400) {
          this.setErrorState(res.data);
        } else {
          this.setState({
            adminUserAnalyticsData: res.data,
          });
          this.setLoaderState(false);
        }
      },
      error: (error) => {
        this.setErrorState(error);
      },
    });
  }

  getAdminUserQueriesData() {
    this.setLoaderState(true);
    this.apiService.getAdminUserQueriesData().subscribe({
      next: (res: any) => {
        if (res.code === 400) {
          this.setErrorState(res.data);
        } else {
          this.setState({
            adminUserQueriesData: res.data,
          });
          this.setLoaderState(false);
        }
      },
      error: (error) => {
        this.setErrorState(error);
      },
    });
  }

  markResolveUserQuery(query_id: number, user_id: number) {
    this.setLoaderState(true);
    this.apiService.markResolveUserQuery(query_id, user_id).subscribe({
      next: (res: any) => {
        if (res.code === 400) {
          this.setErrorState(res.data);
        } else {
          this.getAdminUserAnalyticsData();
          this.getAdminUserQueriesData();
          this.setLoaderState(false);
        }
      },
      error: (error) => {
        this.setErrorState(error);
      },
    });
  }
}
